import { useEffect, useState } from "react";
import { apiClient } from "@/lib/auth";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Tooltip, Legend } from "recharts";
import { Compass, AlertTriangle, ArrowRight, TrendingUp, Download, FileText } from "lucide-react";
import { toast } from "sonner";

function exportAuditCSV(audit, businessName) {
  const rows = [
    ["Métrique", "Valeur"],
    ["Profil", businessName || ""],
    ["Date", new Date().toLocaleDateString("fr-FR")],
    ["Score de visibilité", audit.scores.ai_visibility_score],
    ["Référencement local", audit.scores.local_seo_score],
    ["Engagement social", audit.scores.social_media_score],
    ["Profil complété", audit.scores.profile_completion],
    [""], ["Recommandations"],
    ["Priorité", "Titre", "Détail"],
    ...audit.recommendations.map(r => [r.priority, r.title, r.detail]),
    [""], ["Concurrents"],
    ["Nom", "Visibilité", "Référencement", "Social"],
    ...audit.competitors.map(c => [c.name, c.ai_visibility_score, c.seo, c.social]),
  ];
  const csv = rows.map(r => r.map(v => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = `audit-localpulse-${new Date().toISOString().slice(0,10)}.csv`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
  toast.success("Audit exporté");
}

function Gauge({ value, label }) {
  const r = 42;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <div className="border border-border rounded-2xl p-5 bg-card flex flex-col items-center" data-testid={`gauge-${label}`}>
      <div className="relative h-28 w-28">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth="6" />
          <defs>
            <linearGradient id={`g-${label}`} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="hsl(var(--primary))" />
              <stop offset="100%" stopColor="hsl(250 95% 64%)" />
            </linearGradient>
          </defs>
          <circle cx="50" cy="50" r={r} fill="none" stroke={`url(#g-${label})`} strokeWidth="6"
            strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
            style={{ transition: "stroke-dashoffset 1s cubic-bezier(0.16, 1, 0.3, 1)" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-black font-display">{value}</span>
          <span className="text-[10px] text-muted-foreground">/ 100</span>
        </div>
      </div>
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mt-3 text-center">{label}</p>
    </div>
  );
}

export default function Audit() {
  const [businesses, setBusinesses] = useState([]);
  const [biz, setBiz] = useState("");
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiClient.get("/business").then(r => {
      setBusinesses(r.data);
      if (r.data[0]) setBiz(r.data[0].business_id);
    });
  }, []);

  const run = async () => {
    if (!biz) { toast.error("Sélectionnez un profil"); return; }
    setLoading(true);
    try { const r = await apiClient.post("/audit", { business_id: biz }); setAudit(r.data); toast.success("Diagnostic terminé"); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8" data-testid="audit-page">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary">Diagnostic</p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter mt-2 font-display">
          Audit de <span className="font-serif-italic font-normal">visibilité</span>
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
          Diagnostic complet de votre présence locale, plan d'action priorisé et benchmark face à vos concurrents.
        </p>
      </div>

      <div className="border border-border rounded-2xl p-6 bg-gradient-to-br from-primary/5 to-violet-500/5 flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <p className="text-xs uppercase tracking-wider font-bold text-muted-foreground mb-2">Profil à analyser</p>
          <Select value={biz} onValueChange={setBiz}>
            <SelectTrigger className="rounded-md h-11 bg-card" data-testid="audit-business-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
            <SelectContent>{businesses.map(b => <SelectItem key={b.business_id} value={b.business_id}>{b.business_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Button onClick={run} disabled={loading || !biz} className="rounded-full h-11 px-6 btn-glow" data-testid="run-audit-btn">
          <Compass className={`h-4 w-4 mr-2 ${loading ? "spin-slow" : ""}`} />{loading ? "Analyse en cours…" : "Lancer le diagnostic"}
        </Button>
      </div>

      {!audit && !loading && (
        <div className="border border-dashed border-border rounded-2xl p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 dot-bg opacity-30" />
          <div className="relative">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br from-primary/15 to-violet-500/15 flex items-center justify-center ring-1 ring-border">
              <Compass className="h-6 w-6 text-primary" />
            </div>
            <h3 className="mt-5 text-2xl font-black font-display tracking-tighter">
              Mesurez votre <span className="font-serif-italic font-normal">empreinte locale</span>
            </h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              60 secondes, c'est tout ce qu'il faut pour identifier vos points de croissance.
            </p>
          </div>
        </div>
      )}

      {audit && (
        <div className="space-y-6 fade-up" data-testid="audit-results">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">Diagnostic du <span className="font-semibold text-foreground">{new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</span></p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => exportAuditCSV(audit, businesses.find(b => b.business_id === biz)?.business_name)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-border hover:border-primary/40 hover:text-primary transition-colors"
                data-testid="export-csv-btn">
                <Download className="h-3.5 w-3.5" />CSV
              </button>
              <button
                onClick={() => { window.print(); }}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-border hover:border-primary/40 hover:text-primary transition-colors"
                data-testid="export-pdf-btn">
                <FileText className="h-3.5 w-3.5" />PDF
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Gauge value={audit.scores.ai_visibility_score} label="Score de visibilité" />
            <Gauge value={audit.scores.local_seo_score} label="Référencement local" />
            <Gauge value={audit.scores.social_media_score} label="Engagement social" />
            <Gauge value={audit.scores.profile_completion} label="Profil complété" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 border border-border rounded-2xl p-6 bg-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground">Plan d'action</p>
                  <h3 className="font-bold text-lg mt-1">Recommandations priorisées</h3>
                </div>
                <span className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary font-bold">{audit.recommendations.length} actions</span>
              </div>
              <ul className="space-y-3">
                {audit.recommendations.map((r, i) => {
                  const isHigh = r.priority === "haute";
                  const isMed = r.priority === "moyenne";
                  return (
                    <li key={i} className="group border border-border rounded-xl p-4 hover:border-primary/40 transition-colors">
                      <div className="flex items-start gap-3">
                        <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                          isHigh ? "bg-rose-500/10 text-rose-500" : isMed ? "bg-amber-500/10 text-amber-500" : "bg-primary/10 text-primary"
                        }`}>
                          {isHigh ? <AlertTriangle className="h-4 w-4" /> : <TrendingUp className="h-4 w-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3">
                            <p className="font-semibold text-sm">{r.title}</p>
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${
                              isHigh ? "bg-rose-500/10 text-rose-500" : isMed ? "bg-amber-500/10 text-amber-500" : "bg-primary/10 text-primary"
                            }`}>{r.priority}</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{r.detail}</p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="lg:col-span-2 border border-border rounded-2xl p-6 bg-card">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground">Benchmark</p>
              <h3 className="font-bold text-lg mt-1 mb-4">Vous vs concurrents</h3>
              <div className="h-72 min-h-[288px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={[
                    { dim: "Visibilité", Vous: audit.scores.ai_visibility_score, Concurrents: Math.round(audit.competitors.reduce((a,c)=>a+c.ai_visibility_score,0)/audit.competitors.length) },
                    { dim: "Référencement", Vous: audit.scores.local_seo_score, Concurrents: Math.round(audit.competitors.reduce((a,c)=>a+c.seo,0)/audit.competitors.length) },
                    { dim: "Social", Vous: audit.scores.social_media_score, Concurrents: Math.round(audit.competitors.reduce((a,c)=>a+c.social,0)/audit.competitors.length) },
                    { dim: "Profil", Vous: audit.scores.profile_completion, Concurrents: 70 },
                  ]}>
                    <PolarGrid stroke="hsl(var(--border))" />
                    <PolarAngleAxis dataKey="dim" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <PolarRadiusAxis tick={false} axisLine={false} domain={[0, 100]} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                    <Radar name="Vous" dataKey="Vous" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.35} />
                    <Radar name="Concurrents" dataKey="Concurrents" stroke="hsl(250 95% 64%)" fill="hsl(250 95% 64%)" fillOpacity={0.15} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="border border-border rounded-2xl p-6 bg-card">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground">Concurrents détectés</p>
            <h3 className="font-bold text-lg mt-1 mb-4">Détail comparatif</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2 font-bold">Concurrent</th>
                    <th className="px-4 py-2 font-bold">Visibilité</th>
                    <th className="px-4 py-2 font-bold">Référencement</th>
                    <th className="px-4 py-2 font-bold">Social</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.competitors.map((c, i) => (
                    <tr key={i} className="border-b border-border last:border-0 hover:bg-accent/40 transition-colors">
                      <td className="px-4 py-3 font-semibold">{c.name}</td>
                      <td className="px-4 py-3 tabular-nums">{c.ai_visibility_score}</td>
                      <td className="px-4 py-3 tabular-nums">{c.seo}</td>
                      <td className="px-4 py-3 tabular-nums">{c.social}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
